"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = Gallery;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react2 = require("@linaria/react");
var _patternLibraryUtils = require("@site/pattern-library-utils");
var _section = _interopRequireDefault(require("@site/pattern-library-core/section"));
var _index = _interopRequireDefault(require("../../../elements/other/headline-lockup/index.js"));
var _function = require("../../sections/smart-testimonials-section/function");
var _GalleryControls = _interopRequireDefault(require("./GalleryControls"));
var _GalleryImageItem = _interopRequireDefault(require("./GalleryImageItem"));
var _motionFunctions = require("@site/pattern-library-core/motion-functions");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
/* eslint-disable max-statements */

const AboveGalleryHeader = /*#__PURE__*/(0, _react2.styled)("header")({
  name: "AboveGalleryHeader",
  class: "pl-aibr9c7"
});
const GalleryWrapper = /*#__PURE__*/(0, _react2.styled)("div")({
  name: "GalleryWrapper",
  class: "pl-g1ixjv8s"
});
const vwToPx = (viewportWidth, vw) => vw * viewportWidth / 100;
const extractNum = str => Number(str.replace(/\D/g, ''));

/**
 * Gallery component.
 * @module Gallery
 *
 * @param {Object} props React Props
 * @param {string} props.id The Gallery's id
 * @param {string} props._name The tracking name for the component
 * @param {string} props.title The Gallery's Title
 * @param {number} props.headlineLineLength the lenght in 10s of rems, ie: 1.3 is 13ems of length
 * @param {string} props.nextAriaLabel The aria-label for the "next" buttons
 * @param {string} props.prevAriaLabel The aria-label for the "previous" buttons
 * @param {Array} [items] The Gallery Items to be displayed
 * @param {string} props.verticalMargin Vertical Spacing value
 * @param {string} props.partial Partial Theme
 * @returns {React.ReactElement} Gallery component
 * @public
 */
function Gallery({
  id,
  _name,
  title,
  nextAriaLabel,
  prevAriaLabel,
  items,
  headlineLineLength = 3.0,
  headlineLockup = null,
  verticalMargin = 'none',
  partial
}) {
  const [containerWidth, setContainerWidth] = (0, _react.useState)(0);
  const [slidesWidth, setSlidesWidth] = (0, _react.useState)(0);
  const [gapSize, setGapSize] = (0, _react.useState)(0);
  const [slideCardWidth, setSlideCardWidth] = (0, _react.useState)(0);
  const slidesRef = (0, _react.useRef)();
  const width = (0, _patternLibraryUtils.useWindowSize)();
  const [prevDisabled, setPrevDisabled] = (0, _react.useState)(true);
  const [nextDisabled, setNextDisabled] = (0, _react.useState)(false);
  const dir = () => {
    if (slidesRef?.current) {
      return window.getComputedStyle(slidesRef.current).direction;
    }
  };
  const getEndPoint = (side, direction) => {
    if (direction === 'rtl') {
      return side === 'right' ? -(containerWidth - slidesWidth - vwToPx(containerWidth, 8)) : 0;
    }
    return side === 'left' ? containerWidth - slidesWidth - vwToPx(containerWidth, 8) : 0;
  };
  const setTranslateX = (element, value) => {
    element.style.transform = `translateX(${value}px)`;
  };
  const handlePrevButtonClick = () => {
    const xPosition = (0, _function.translateXForElement)(slidesRef.current);
    let newXPosition = xPosition + slideCardWidth + gapSize;
    const endPoint = getEndPoint('right', dir());
    newXPosition = newXPosition > endPoint ? endPoint : newXPosition;
    setTranslateX(slidesRef.current, newXPosition);
    const disable = newXPosition === endPoint;
    setPrevDisabled(dir() !== 'rtl' && disable);
    setNextDisabled(dir() === 'rtl' && disable);
  };
  const handleNextButtonClick = () => {
    const xPosition = (0, _function.translateXForElement)(slidesRef.current);
    let newXPosition = xPosition - slideCardWidth - gapSize;
    const endPoint = getEndPoint('left', dir());
    newXPosition = newXPosition < endPoint ? endPoint : newXPosition;
    setTranslateX(slidesRef.current, newXPosition);
    const disable = newXPosition === endPoint;
    setPrevDisabled(dir() === 'rtl' && disable);
    setNextDisabled(dir() !== 'rtl' && disable);
  };
  (0, _react.useEffect)(() => {
    const slider = slidesRef.current;
    setSlidesWidth(slider.clientWidth);
    setContainerWidth(window.innerWidth);
    setSlideCardWidth(slider.children[0].clientWidth);
    setGapSize(extractNum(window.getComputedStyle(slider).getPropertyValue('column-gap')));
    if (window.innerWidth >= slider.clientWidth) {
      setNextDisabled(true);
      setPrevDisabled(true);
    }
    const selectors = ['h3[data-cy=gallery-item-headline-string]>span', 'p[data-cy=gallery-item-headline-description]'];
    selectors.forEach(selector => {
      const itemElement = slider.querySelectorAll(selector);
      const elementHeights = itemElement && [...itemElement].map(element => element.clientHeight);
      const maxHeight = Math.max(...elementHeights);
      itemElement.forEach(heading => heading.style.minHeight = `${maxHeight}px`);
    });
  }, [width]);
  return /*#__PURE__*/_react.default.createElement(_section.default, {
    id: id,
    _name: _name,
    dataCy: "gallery-section",
    partial: partial,
    padding: "none",
    verticalMargin: verticalMargin
  }, /*#__PURE__*/_react.default.createElement(AboveGalleryHeader, null, /*#__PURE__*/_react.default.isValidElement(headlineLockup) ? (/*#__PURE__*/_react.default.cloneElement(headlineLockup, {
    ...headlineLockup.props,
    headlineSize: 'h3',
    headlineTag: 'h2',
    textAlign: 'start'
  })) : title && /*#__PURE__*/_react.default.createElement(_index.default, {
    headlineSize: "h3",
    headlineTag: "h2",
    textAlign: "start",
    heading: title,
    headlineLineLength: headlineLineLength
  }), /*#__PURE__*/_react.default.createElement(_GalleryControls.default, {
    prevButtonAriaLabel: prevAriaLabel,
    nextButtonAriaLabel: nextAriaLabel,
    prevCallback: dir() === 'rtl' ? handleNextButtonClick : handlePrevButtonClick,
    nextCallback: dir() === 'rtl' ? handlePrevButtonClick : handleNextButtonClick,
    disablePrev: prevDisabled,
    disableNext: nextDisabled
  })), /*#__PURE__*/_react.default.createElement(GalleryWrapper, {
    ref: slidesRef
  }, items?.map((item, i) => {
    return (
      // TODO: CMS-30265: Remove this logic entirely. Should only render item as a React Node.
      /*#__PURE__*/_react.default.isValidElement(item) ? item : /*#__PURE__*/_react.default.createElement(_GalleryImageItem.default, (0, _extends2.default)({}, item, {
        key: i
      }))
    );
  })));
}
Gallery.propTypes = {
  id: _propTypes.default.string,
  _name: _propTypes.default.string,
  title: _propTypes.default.string,
  headlineLineLength: _propTypes.default.number,
  nextAriaLabel: _propTypes.default.string,
  prevAriaLabel: _propTypes.default.string,
  items: _propTypes.default.arrayOf(_propTypes.default.shape(_GalleryImageItem.default.propTypes)),
  partial: _propTypes.default.string,
  ..._patternLibraryUtils.marginProps
};